import { z } from 'zod';

/**
 * Octopus — the MCTS lens-design engine (Photonium-RL-MCMC repo), deployed as
 * its own Railway service and reached only through the admin proxy routes.
 *
 * The engine owns these payloads. We mirror the envelope the admin UI branches
 * on and leave the search trace itself opaque: its shape belongs to the
 * research repo and moves with the checkpoint, so a copy here would be a
 * second source of truth that silently rots.
 */

/**
 * The search and merit settings a run uses unless the request overrides them.
 * Keys are the engine's own config field names; the overridable subset is
 * accepted back by /run under the same names.
 */
export const OctopusConfigSchema = z.object({
  wavelength_mode: z.enum(['primary', 'all']),
  /** µm. What a run traces when it names no wavelengths of its own. */
  wavelengths: z.array(z.number()),
  target_rms_spot: z.number().nullable(),
  /** Fractions of the half-field. Fixed in count: the policy net's
   *  observation width is built from it. */
  fields: z.array(z.number()),
  field_weights: z.array(z.number()),
  n_rays: z.number().int(),
  optimize_vd: z.boolean(),
  /** LM iterations per candidate; `lm_commit_iter` is the committed action's. */
  lm_max_iter: z.number().int(),
  lm_commit_iter: z.number().int(),
  stop_rel_improvement: z.number().nullable(),
  stop_merit: z.number().nullable(),
  stop_patience: z.number().int(),
  max_steps_per_episode: z.number().int(),
  num_simulations: z.number().int(),
  max_num_considered_actions: z.number().int(),
  gumbel_scale: z.number(),
  /** Aspect-ratio penalty: [lambda, lo, hi]; lambda 0 disables it. */
  aspect: z.tuple([z.number(), z.number(), z.number()]),
});

export type OctopusConfig = z.infer<typeof OctopusConfigSchema>;

export const OctopusPingSchema = z.object({
  ok: z.boolean(),
  device: z.string(),
  ckpt: z.string(),
  /** Polychromatic instance (merit over all wavelengths) vs mono d-line. */
  poly: z.boolean(),
  /** Non-null only while a search holds the engine's single-job lock. */
  progress: z.string().nullable(),
  config: OctopusConfigSchema,
});

export type OctopusPing = z.infer<typeof OctopusPingSchema>;

export const OctopusRunStartedSchema = z.object({ job: z.string() });

export const OctopusJobSchema = z.discriminatedUnion('status', [
  z.object({
    status: z.literal('running'),
    progress: z.string().nullable(),
    device: z.string(),
  }),
  z.object({ status: z.literal('done'), trace: z.unknown() }),
  z.object({ status: z.literal('error'), error: z.string() }),
  /** Stopped by /cancel — a deliberate end, not a failure. The moves made
   *  before the cancel are a valid partial result, carried in `trace`. */
  z.object({ status: z.literal('cancelled'), detail: z.string(), trace: z.unknown() }),
]);
export type OctopusJob = z.infer<typeof OctopusJobSchema>;

/**
 * Which live searches the cancel reached. `running` is what actually holds the
 * lock, so a caller whose named job missed (engine restarted, another tab's
 * run) can tell a stale id from nothing-running.
 */
export const OctopusCancelSchema = z.object({
  cancelled: z.array(z.string()),
  running: z.array(z.string()),
  note: z.string(),
});
